import { createPopper } from '@popperjs/core'

const createTooltipElement = (id, content) => {
  const tooltip = document.createElement('div')
  tooltip.id = id
  tooltip.classList.add('tooltip', 'bs-tooltip-auto', 'fade')
  tooltip.setAttribute('role', 'tooltip')
  const arrow = document.createElement('div')
  arrow.classList.add('tooltip-arrow')
  arrow.dataset.popperArrow = ''
  const inner = document.createElement('div')
  inner.classList.add('tooltip-inner')
  inner.textContent = content
  tooltip.append(arrow, inner)
  return tooltip
}

const addTooltipElement = (el, state) => {
  // Never show a tooltip for an element that is no longer in the DOM
  if (!document.body.contains(el)) return
  // Cancel any pending hide so quick hover cycles don't leave it stuck
  clearTimeout(state.hideTimeout)

  if (!state.attached) {
    el.setAttribute('aria-describedby', state.uid)
    document.body.appendChild(state.tooltip)
    state.popperInstance = createPopper(el, state.tooltip, state.popperOptions)
    state.attached = true
  }

  clearTimeout(state.showTimeout)
  state.showTimeout = setTimeout(() => {
    state.tooltip.classList.add('show')
    state.visible = true
  }, 1)
}

const removeTooltipElement = (_el, state) => {
  clearTimeout(state.showTimeout)
  state.visible = false

  if (!state.attached) return
  state.tooltip.classList.remove('show')

  clearTimeout(state.hideTimeout)
  state.hideTimeout = setTimeout(() => {
    state.tooltip.remove()
    if (state.popperInstance) {
      state.popperInstance.destroy()
      state.popperInstance = null
    }
    state.attached = false
  }, 300)
}

export default {
  name: 'c-tooltip',
  mounted(el, binding) {
    const value = binding.value || {}
    const content = typeof value === 'string' ? value : (value.content ?? '')
    const trigger = value.trigger ?? 'hover'
    const offset = value.offset ?? [0, 6]
    const placement = value.placement ?? 'top'
    const popperOptions = {
      placement,
      modifiers: [
        {
          name: 'offset',
          options: { offset },
        },
      ],
    }

    const uid = `tooltip-fixed-${Math.random().toString(36).slice(2)}`
    // State lives on the element itself so cleanup works even after Vue
    // re-renders recreate directive bindings (CoreUI's version loses the
    // tooltip reference here and leaves orphaned tooltips on screen).
    const state = {
      uid,
      tooltip: createTooltipElement(uid, content),
      popperOptions,
      popperInstance: null,
      showTimeout: null,
      hideTimeout: null,
      attached: false,
      visible: false,
    }
    el.__cTooltipState = state

    const onEnter = () => addTooltipElement(el, state)
    const onLeave = () => removeTooltipElement(el, state)

    if (trigger.includes('click')) {
      el.addEventListener('click', () => {
        if (state.visible) {
          removeTooltipElement(el, state)
        } else {
          addTooltipElement(el, state)
        }
      })
    }
    if (trigger.includes('focus')) {
      el.addEventListener('focus', onEnter)
      el.addEventListener('blur', onLeave)
    }
    if (trigger.includes('hover')) {
      el.addEventListener('mouseenter', onEnter)
      el.addEventListener('mouseleave', onLeave)
    }
  },
  unmounted(el) {
    const state = el.__cTooltipState
    if (!state) return
    clearTimeout(state.showTimeout)
    clearTimeout(state.hideTimeout)
    el.removeAttribute('aria-describedby')
    state.tooltip.remove()
    if (state.popperInstance) {
      state.popperInstance.destroy()
    }
    delete el.__cTooltipState
  },
}
